import { css, Styles, withStyles } from '@recruit-robin/robin-theme/web-styles';

import { af } from '../theme';
import type { ApplyTextRole, ApplyTextTone } from './ApplyText.types';

export type ApplyTextStyleProps = {
  role: ApplyTextRole;
  tone: ApplyTextTone;
  align?: 'start' | 'center';
};

const TONE_COLOR: Record<ApplyTextTone, string> = {
  ink: af.ink,
  'ink-soft': af.inkSoft,
  muted: af.muted,
  paper: af.paper,
  brand: af.brand,
};

/**
 * The roles on the client kit's five sizes (see `ApplyText.types.ts`).
 *
 * Each size carries the line height the kit pairs with it — 28/32, 22/26, 16/24, 14/20,
 * 11/16 — in pixels, so the pair is exact rather than a ratio that rounds near it. Sizes hold
 * across window classes: nothing shrinks on phone. Uppercase is applied here, as a style:
 * copy is written in sentence case and never typed in caps.
 *
 * Weight is what picks the ZT Talk face (see `fonts.ts`): 900 is the Black, 600 lands on Bold
 * Semi Expanded, 400 on Semi Expanded.
 *
 * Tracking: negative on Display and Title, 0.09em on every capital — "capitals are always ZT
 * Talk Black, at 14 or 11, with 0.09em letter spacing" — and none anywhere else.
 */
const ROLE_CSS: Record<ApplyTextRole, ReturnType<typeof css>> = {
  display: css`
    font-size: 28px;
    line-height: 32px;
    letter-spacing: -0.02em;
    font-weight: 900;
  `,
  title: css`
    font-size: 22px;
    line-height: 26px;
    letter-spacing: -0.015em;
    font-weight: 900;
  `,
  item: css`
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
  `,
  body: css`
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
  `,
  sub: css`
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
  `,
  /** Small in Bold: the time slots, the call channel, and the day the slots belong to. */
  choice: css`
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
  `,
  label: css`
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.09em;
    font-weight: 900;
    text-transform: uppercase;
  `,
  /** Label in Bold, sentence case: the progress row and the chips. */
  meta: css`
    font-size: 11px;
    line-height: 16px;
    font-weight: 600;
    /*
     * Explicit: some robin-theme supporting variants bake in uppercase, and this role is
     * sentence case. Only the label and cta roles may transform case, and both say so.
     */
    text-transform: none;
    /* Step counters change every screen; proportional digits make the row twitch. */
    font-variant-numeric: tabular-nums;
  `,
  /** Label in Semi Expanded — the kit's small print: notes under a block, and the footer. */
  note: css`
    font-size: 11px;
    line-height: 16px;
    font-weight: 400;
    /* Sentence case, for the same reason as meta above. */
    text-transform: none;
  `,
  /**
   * The CTA: Small in Black capitals. Line height 1 rather than the size's 20px — it is only
   * ever one line, centred in a button that sets its own height.
   */
  cta: css`
    font-size: 14px;
    line-height: 1;
    letter-spacing: 0.09em;
    font-weight: 900;
    text-transform: uppercase;
  `,
};

const styles = {
  text: ({ props: { role, tone, align } }) => css`
    /*
     * robin-theme's variants each carry a little tracking of their own (0.005em–0.01em). The kit
     * has none outside the roles above that set it, and it widens a line by a visible 1–2%.
     */
    letter-spacing: 0;
    ${ROLE_CSS[role]};
    color: ${TONE_COLOR[tone]};
    ${align === 'center' && 'text-align: center;'}
  `,
  /**
   * Headings carry the browser's own size and margin. The role above is the only
   * source of both, so the element contributes semantics and nothing else.
   */
  wrapper: css`
    margin: 0;
    font: inherit;
    color: inherit;
  `,
} satisfies Styles<ApplyTextStyleProps>;

export const useStyles = withStyles(styles);
